import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FieldArrayType, FormlyModule } from '@ngx-formly/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { UserRolesService } from 'app/features/admin/user-roles/user-roles.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { UserStore } from 'app/core/services/user.store';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { debounceTime, takeUntil } from 'rxjs';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { DateHelperService } from '../../services/date-helper.service';
import type { RouteDuration, RoutePointTime, TravelSegment } from './route.types';
import { RouteMapService } from '../../../core/services/route-map.service';
import {
  DETAILS_SERVICE_TOKEN,
  DetailsService,
} from '../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { RouteMapComponent } from '../route-map.component';

@Component({
  selector: 'formly-route-section',
  template: `
    <div class="my-4" [attr.data-cy]="field.props?.keyPath ?? null">
      <!-- Header with label, total time and map toggle -->
      @if (props.label && !props.hideLabel) {
        <div class="flex items-center justify-between mb-2">
          <h3 class="flex items-center gap-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </h3>
          <div class="flex items-center gap-2">
            <!-- Total route time -->
            <span class="text-sm text-hint flex items-center gap-1" matTooltip="Totale route tijd">
              <mat-icon class="icon-size-4">schedule</mat-icon>
              {{ totalRouteDuration()?.formatted || '--:--' }}
            </span>
            <!-- Map toggle -->
            <button
              type="button"
              mat-icon-button
              (click)="toggleMapSidebar()"
              [attr.aria-label]="mapSidebarVisible() ? 'Verberg kaart' : 'Toon kaart'"
              [matTooltip]="mapSidebarVisible() ? 'Verberg kaart' : 'Toon kaart'">
              <mat-icon>map</mat-icon>
            </button>
          </div>
        </div>
      }
      @if (props.description) {
        <p class="mb-2">{{ props.description }}</p>
      }

      <!-- Main content with optional sidebar -->
      <div class="flex gap-4">
        <!-- Left: Form fields with timeline -->
        <div class="flex-1 min-w-0">
          <!-- Departure point -->
          @if (baseDepartureDate()) {
            <div class="flex items-start gap-3 mb-0">
              <!-- Timeline column: dot + line to first stop -->
              <div class="flex flex-col items-center w-12 flex-shrink-0">
                <!-- Departure dot -->
                <div class="w-[18px] h-[18px] rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <mat-icon class="!w-2.5 !h-2.5 text-white text-[10px]">location_on</mat-icon>
                </div>
                <!-- Connecting line with travel time -->
                @if (field?.fieldGroup?.length >= 1) {
                  <div class="flex-1 flex flex-col items-center min-h-[40px] w-full">
                    <div class="w-0.5 flex-1 bg-primary/30"></div>
                    @if (travelTimesBetweenStops()[0]?.formattedDuration; as duration) {
                      <span class="text-[10px] text-hint bg-card px-1 -my-2 z-10">{{ duration }}</span>
                    }
                    <div class="w-0.5 flex-1 bg-primary/30"></div>
                  </div>
                }
              </div>
              <!-- Departure info -->
              <div class="flex-1 pb-2">
                <div class="text-sm font-medium leading-3">Vertrek</div>
                <div class="text-xs text-hint leading-3">
                  {{ baseDepartureDate() | date: 'EEEE d MMM yyyy, HH:mm' : '' : 'nl' }}
                </div>
              </div>
            </div>
          }

          <!-- Route items -->
          @if (field?.fieldGroup?.length >= 1) {
            <div class="inline-block w-full" cdkDropList (cdkDropListDropped)="drop($event)">
              <!-- Object identity, not field.id — see repeat.component.ts for the serialized-id rationale. -->
              @for (fieldGroupField of field.fieldGroup; let i = $index; let last = $last; track fieldGroupField) {
                @if (field.className !== 'hidden') {
                  <div
                    cdkDrag
                    [cdkDragDisabled]="disableDrag || hideAddButton()"
                    class="repeater-item flex items-stretch gap-3 mb-0">
                    <!-- Timeline column: dot + time + line to next stop -->
                    <div class="flex flex-col items-center w-12 flex-shrink-0">
                      <!-- Stop dot with number -->
                      <div
                        class="w-[18px] h-[18px] rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-on-primary pt-px">
                        {{ i + 1 }}
                      </div>
                      <!-- Arrival time (and end time if duration set) under the dot -->
                      @if (calculatedDates().get(i); as pointTime) {
                        <span class="text-[10px] text-hint leading-tight mt-0.5 whitespace-nowrap">
                          {{ pointTime.calculatedDate | date: 'HH:mm' }}
                          @if (getStopEndTime(i); as endTime) {
                            - {{ endTime | date: 'HH:mm' }}
                          }
                        </span>
                        @if (pointTime.dateLabel) {
                          <span class="text-[9px] text-warning leading-tight">{{ pointTime.dateLabel }}</span>
                        }
                      }
                      <!-- Connecting line with travel time -->
                      <div class="flex-1 flex flex-col items-center min-h-[30px] w-full">
                        <div class="w-0.5 flex-1 bg-primary/30"></div>
                        @if (!last && travelTimesBetweenStops()[i + 1]?.formattedDuration; as duration) {
                          <span class="text-[10px] text-hint bg-card px-1 -my-1.5 z-10">{{ duration }}</span>
                          <div class="w-0.5 flex-1 bg-primary/30"></div>
                        } @else if (last && travelTimeToDestination()?.formattedDuration; as destDuration) {
                          <!-- Last item: show travel time to destination -->
                          <span class="text-[10px] text-hint bg-card px-1 -my-1.5 z-10">{{ destDuration }}</span>
                          <div class="w-0.5 flex-1 bg-primary/30"></div>
                        }
                      </div>
                    </div>

                    <!-- Order controls -->
                    <div class="flex flex-col items-center flex-shrink-0 pt-1">
                      @if (!hideAddButton() && !form.disabled && !field.props.disabled) {
                        <!-- Up arrow (not on first item) -->
                        @if (i > 0) {
                          <button
                            type="button"
                            mat-icon-button
                            class="!h-8 !w-8 !min-h-0 !min-w-0"
                            (click)="moveUp(i)"
                            aria-label="Verplaats naar boven"
                            matTooltip="Verplaats naar boven">
                            <mat-icon class="icon-size-5">keyboard_arrow_up</mat-icon>
                          </button>
                        } @else {
                          <div class="h-8 w-8"></div>
                        }

                        <!-- Drag handle -->
                        @if (!disableDrag) {
                          <mat-icon class="text-hint cursor-move" cdkDragHandle svgIcon="mat_outline:drag_indicator">
                          </mat-icon>
                        }

                        <!-- Down arrow (not on last item) -->
                        @if (i < field.fieldGroup.length - 1) {
                          <button
                            type="button"
                            mat-icon-button
                            class="!h-8 !w-8 !min-h-0 !min-w-0"
                            (click)="moveDown(i)"
                            aria-label="Verplaats naar beneden"
                            matTooltip="Verplaats naar beneden">
                            <mat-icon class="icon-size-5">keyboard_arrow_down</mat-icon>
                          </button>
                        } @else {
                          <div class="h-8 w-8"></div>
                        }
                      }
                    </div>

                    <!-- Field content -->
                    <div class="flex-1 min-w-0 relative pb-2">
                      <formly-field [field]="fieldGroupField"></formly-field>
                      <!-- Warning if arrival time is before previous activity end -->
                      @if (calculatedDates().get(i); as pointTime) {
                        @if (pointTime.isBelowMinimum) {
                          <div class="text-xs text-warn mt-1">Aankomsttijd is eerder dan einde vorige activiteit</div>
                        }
                      }
                    </div>

                    <!-- Remove button -->
                    @if (
                      !form.disabled &&
                      !fieldGroupField.props?.disabled &&
                      props.initialCount < field.fieldGroup.length &&
                      !hideAddButton()
                    ) {
                      <button type="button" class="flex-0 max-h-fit" mat-icon-button aria-label="Routepunt verwijderen" (click)="remove(i)">
                        <mat-icon class="icon-size-4">close</mat-icon>
                      </button>
                    }
                  </div>
                }
              }
            </div>
          } @else {
            <p class="text-sm text-accent">Geen {{ props.label ? props.label.toLowerCase() : 'items' }} aangemaakt</p>
          }

          <!-- Destination point -->
          @if (baseDepartureDate()) {
            <div class="flex items-start gap-3">
              <!-- Timeline column: destination dot + time -->
              <div class="flex flex-col items-center w-12 flex-shrink-0">
                <!-- Destination dot -->
                <div class="w-[18px] h-[18px] rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <mat-icon class="!w-2.5 !h-2.5 text-white text-[10px]">flag</mat-icon>
                </div>
                <!-- Arrival time under the dot -->
                @if (destinationTime(); as destTime) {
                  <span class="text-[10px] text-hint leading-tight mt-0.5">
                    {{ destTime | date: 'HH:mm' }}
                  </span>
                  @if (!isSameDay(baseDepartureDate(), destTime)) {
                    <span class="text-[9px] text-warning leading-tight">{{
                      destTime | date: 'd MMM' : '' : 'nl'
                    }}</span>
                  }
                }
              </div>
              <!-- Destination info -->
              <div class="flex-1 pt-1">
                <div class="text-sm font-medium leading-3">Bestemming</div>
                <div class="text-xs text-hint leading-3">
                  @if (destinationTime(); as destTime) {
                    {{ destTime | date: 'EEEE d MMM yyyy, HH:mm' : '' : 'nl' }}
                  } @else {
                    --:--
                  }
                </div>
              </div>
            </div>
          }

          <!-- Add button -->
          @if (!form.disabled && !field.props.disabled && !hideAddButton()) {
            <div class="pb-4 mt-4 ml-[60px]">
              <button
                type="button"
                class="small-button dd-action-button"
                mat-flat-button
                (click)="add(field.fieldGroup?.length, getDefaultModelForIndex(field.fieldGroup?.length))">
                <mat-icon>add</mat-icon>
                {{ props.addText ? props.addText : 'Locatie toevoegen' }}
              </button>
            </div>
          }
        </div>

        <!-- Right: Inline map (alleen in modal context) -->
        @if (isModalContext() && showRouteMap() && mapSidebarVisible()) {
          <div class="w-[400px] flex-shrink-0">
            <app-route-map
              [height]="'h-[400px]'"
              [selectionModeActiveEnabled]="false"
              [locations]="locationsData()"></app-route-map>
          </div>
        }
      </div>
    </div>
  `,
  imports: [
    DatePipe,
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    InfoTooltipComponent,
    RouteMapComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .custom-placeholder {
      background: #ccc;
      border: dotted 3px #999;
      min-height: 60px;
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }
  `,
})
export class FormlyRouteTypeComponent extends FieldArrayType implements OnInit {
  disableDrag = true;
  readonly #route = inject(ActivatedRoute);
  readonly #params = toSignal(this.#route.paramMap);
  readonly #moduleId = computed(() => this.options.formState?.entity?.id);
  readonly #userStore = inject(UserStore);
  readonly #userRolesService = inject(UserRolesService);
  readonly #formService = inject(FormService);
  readonly #destroy$ = injectDestroy();
  readonly #routeMapService = inject(RouteMapService);
  readonly #detailsService = inject(DETAILS_SERVICE_TOKEN, { optional: true }) as DetailsService | null;

  // ==========================================
  // Configurable field keys (from props, with defaults)
  // ==========================================
  readonly departureDateFieldKey = computed(() => this.props.departureDateField || 'departure-time');
  readonly departureLocationFieldKey = computed(() => this.props.departureLocationField || 'departure-location');
  readonly destinationLocationFieldKey = computed(() => this.props.destinationLocationField || 'destination-location');
  readonly destinationTimeFieldKey = computed(() => this.props.destinationTimeField || 'destination-time');

  // ==========================================
  // State signals
  // ==========================================
  readonly locationsData = signal<unknown[]>([]);
  readonly hideAddButton = signal(false);
  readonly #localMapVisible = signal(false);
  readonly isModalContext = computed(() => this.#detailsService?.isModal() ?? false);
  readonly mapSidebarVisible = computed(() =>
    this.isModalContext() ? this.#localMapVisible() : this.#routeMapService.sidebarVisible()
  );
  readonly totalRouteDuration = signal<RouteDuration | null>(null);
  readonly baseDepartureDate = signal<Date | null>(null);
  readonly previousDepartureDate = signal<Date | null>(null);
  readonly calculatedDates = signal<Map<number, RoutePointTime>>(new Map());
  readonly #destinationTimeValue = signal<Date | null>(null);

  // Guard to prevent infinite loops during programmatic updates
  #isUpdatingTimes = false;

  // ==========================================
  // Computed
  // ==========================================
  readonly showRouteMap = computed(() => this.locationsData().length > 0);

  /**
   * Computed signal that calculates travel times between consecutive stops.
   * Includes: departure → first stop, and stop N → stop N+1
   */
  readonly travelTimesBetweenStops = computed<TravelSegment[]>(() => {
    const segments: TravelSegment[] = [];
    const baseDate = this.baseDepartureDate();
    const calcDates = this.calculatedDates();

    if (!baseDate) {
      return segments;
    }

    // Get all route items
    const fieldGroupLength = this.field?.fieldGroup?.length ?? 0;

    for (let i = 0; i < fieldGroupLength; i++) {
      let fromTime: Date | null = null;
      let toTime: Date | null = null;

      if (i === 0) {
        // Departure → first stop
        fromTime = baseDate;
        toTime = calcDates.get(0)?.calculatedDate ?? null;
      } else {
        // Previous stop (end of activity) → current stop
        const prevPointTime = calcDates.get(i - 1);
        if (prevPointTime?.calculatedDate) {
          // Get the end time of previous stop (arrival + duration)
          const prevFieldGroup = this.field.fieldGroup?.[i - 1];
          const prevDuration = prevFieldGroup?.model?.['duration'];

          if (prevDuration) {
            const durationMinutes = DateHelperService.timeStringToMinutes(prevDuration);
            fromTime = DateHelperService.addMinutes(prevPointTime.calculatedDate, durationMinutes);
          } else {
            fromTime = prevPointTime.calculatedDate;
          }
        }
        toTime = calcDates.get(i)?.calculatedDate ?? null;
      }

      if (fromTime && toTime) {
        const travelMinutes = DateHelperService.calculateTimeDifferenceMinutes(fromTime, toTime);
        segments.push({
          fromIndex: i === 0 ? -1 : i - 1,
          toIndex: i,
          travelMinutes: travelMinutes >= 0 ? travelMinutes : null,
          formattedDuration: travelMinutes >= 0 ? DateHelperService.formatDurationMinutes(travelMinutes) : null,
        });
      } else {
        segments.push({
          fromIndex: i === 0 ? -1 : i - 1,
          toIndex: i,
          travelMinutes: null,
          formattedDuration: null,
        });
      }
    }

    return segments;
  });

  /**
   * Computed signal that calculates the travel time from the last stop to the destination.
   */
  readonly travelTimeToDestination = computed<TravelSegment | null>(() => {
    const destTime = this.destinationTime();
    if (!destTime) return null;

    const fieldGroupLength = this.field?.fieldGroup?.length ?? 0;
    if (fieldGroupLength === 0) {
      // No stops - travel from departure to destination
      const baseDate = this.baseDepartureDate();
      if (!baseDate) return null;

      const travelMinutes = DateHelperService.calculateTimeDifferenceMinutes(baseDate, destTime);
      return {
        fromIndex: -1,
        toIndex: -2, // -2 indicates destination
        travelMinutes: travelMinutes >= 0 ? travelMinutes : null,
        formattedDuration: travelMinutes >= 0 ? DateHelperService.formatDurationMinutes(travelMinutes) : null,
      };
    }

    // Get the end time of the last stop
    const lastIndex = fieldGroupLength - 1;
    const lastPointTime = this.calculatedDates().get(lastIndex);
    if (!lastPointTime?.calculatedDate) return null;

    const lastFieldGroup = this.field.fieldGroup?.[lastIndex];
    const lastDuration = lastFieldGroup?.model?.['duration'];

    let fromTime = lastPointTime.calculatedDate;
    if (lastDuration) {
      const durationMinutes = DateHelperService.timeStringToMinutes(lastDuration);
      fromTime = DateHelperService.addMinutes(lastPointTime.calculatedDate, durationMinutes);
    }

    const travelMinutes = DateHelperService.calculateTimeDifferenceMinutes(fromTime, destTime);
    return {
      fromIndex: lastIndex,
      toIndex: -2, // -2 indicates destination
      travelMinutes: travelMinutes >= 0 ? travelMinutes : null,
      formattedDuration: travelMinutes >= 0 ? DateHelperService.formatDurationMinutes(travelMinutes) : null,
    };
  });

  /**
   * Computed signal that returns the destination time.
   * Uses the backing signal which is updated when the form changes.
   */
  readonly destinationTime = computed<Date | null>(() => this.#destinationTimeValue());

  // ==========================================
  // Lifecycle
  // ==========================================
  ngOnInit() {
    this.disableDrag =
      !this.#userRolesService.checkIfHasEditPermission(this.#userStore.currentUserPermissions(), this.#moduleId()) ||
      this.field.props.disabled;

    // IMPORTANT: Initialize baseDepartureDate FIRST (before adding route items)
    // This ensures that when route items are added via addInitialCount(),
    // the initializeArrivalTimeDate() method has access to the departure date from prefill
    const initialDepartureDate = this.getBaseDepartureDate();
    this.baseDepartureDate.set(initialDepartureDate);
    this.previousDepartureDate.set(initialDepartureDate);

    if (this.field.formControl.controls.length === 0) {
      this.addInitialCount();
    }
    if (this.props.initialCount === undefined) {
      this.props.initialCount = 0;
    }

    // Watch route field value changes
    this.field.formControl.valueChanges.pipe(debounceTime(300), takeUntil(this.#destroy$)).subscribe(() => {
      if (this.#isUpdatingTimes) return;
      this.#isUpdatingTimes = true;
      try {
        this.setLocationsData();
        this.calculateRouteDates();
        this.calculateTotalDuration();
      } finally {
        this.#isUpdatingTimes = false;
      }
    });

    // Setup sibling field watchers
    this.setupDateWatchers();

    // Initial calculations - wrapped in guard to prevent infinite loop
    // when calculateRouteDates triggers setValue which triggers watchers
    this.#isUpdatingTimes = true;
    try {
      // If departure date was already set (e.g., via prefill) and we have route items,
      // initialize any empty arrival-times now
      if (initialDepartureDate && this.field.fieldGroup?.length > 0) {
        this.initializeEmptyArrivalTimes(initialDepartureDate);
      }

      this.setLocationsData();
      this.calculateRouteDates();
      this.refreshDestinationTime();
    } finally {
      this.#isUpdatingTimes = false;
    }

    if (this.props.hideAddButtonForRoles != null && Object.keys(this.props.hideAddButtonForRoles).length > 0) {
      this.hideAddButton.set(this.field.props.hideAddButtonForRoles[this.#userStore.currentUserId()]);
    }
  }

  // ==========================================
  // Drag & Drop / Reorder
  // ==========================================
  drop(event: CdkDragDrop<string[]>) {
    const modelCopy = this.model;
    moveItemInArray(modelCopy, event.previousIndex, event.currentIndex);
    this.field.formControl.patchValue(modelCopy);
    this.options.build(this.field);
  }

  moveUp(index: number): void {
    if (index <= 0) return;
    this.reorderItems(index, index - 1);
  }

  moveDown(index: number): void {
    if (index >= this.field.fieldGroup.length - 1) return;
    this.reorderItems(index, index + 1);
  }

  private reorderItems(fromIndex: number, toIndex: number): void {
    const modelCopy = this.model;
    moveItemInArray(modelCopy, fromIndex, toIndex);
    this.field.formControl.patchValue(modelCopy);
    this.options.build(this.field);
  }

  // ==========================================
  // Map Sidebar
  // ==========================================
  toggleMapSidebar(): void {
    if (this.isModalContext()) {
      // In modal: use local state only
      this.#localMapVisible.update(v => !v);
    } else {
      // Normal page: use global sidebar
      this.#routeMapService.toggleSidebar();
    }
  }

  // ==========================================
  // Location Data for Map
  // ==========================================
  setLocationsData() {
    // Get departure and destination from configured sibling fields
    const departureAddress = this.getSiblingFieldValue(this.departureLocationFieldKey());
    const destinationAddress = this.getSiblingFieldValue(this.destinationLocationFieldKey());

    // Get route points with index for numbering
    const routeLocations =
      this.field.fieldGroup?.map((f, index) => {
        const model = f.model;
        return {
          ...model,
          locationType: 'route-item' as const,
          routeIndex: index + 1, // 1-based numbering for display
        };
      }) ?? [];

    // Build locations array: [departure, ...routePoints, destination]
    const locations: unknown[] = [];

    if (departureAddress) {
      locations.push({
        location: departureAddress,
        locationType: 'departure' as const,
      });
    }

    locations.push(...routeLocations);

    if (destinationAddress) {
      locations.push({
        location: destinationAddress,
        locationType: 'destination' as const,
      });
    }

    this.locationsData.set(locations);
    this.#routeMapService.setLocations(locations);
  }

  // ==========================================
  // Date/Time Calculations
  // ==========================================
  private setupDateWatchers(): void {
    // Use parent form valueChanges for robust change detection
    // This avoids issues with formControl references changing after form rebuilds
    const parentForm = this.field?.parent?.formControl;
    if (parentForm) {
      // Watch all parent form changes and recalculate
      parentForm.valueChanges.pipe(debounceTime(300), takeUntil(this.#destroy$)).subscribe(() => {
        // Guard against re-entry during programmatic updates
        if (this.#isUpdatingTimes) return;

        this.#isUpdatingTimes = true;
        try {
          const newDepartureDate = this.getBaseDepartureDate();
          const previousDate = this.previousDepartureDate();

          if (newDepartureDate) {
            if (previousDate && previousDate.getTime() !== newDepartureDate.getTime()) {
              // Departure date changed - sync existing times
              this.syncTimesWithDepartureDate(previousDate, newDepartureDate);
            } else if (!previousDate) {
              // Departure date set for the first time - initialize empty arrival-times
              this.initializeEmptyArrivalTimes(newDepartureDate);
            }
          }

          this.previousDepartureDate.set(newDepartureDate);
          this.baseDepartureDate.set(newDepartureDate);
          this.calculateRouteDates();
          this.calculateTotalDuration();
          this.setLocationsData();
          this.refreshDestinationTime();
        } finally {
          this.#isUpdatingTimes = false;
        }
      });
    }

    // Setup watchers for existing route items
    this.setupRouteItemWatchers();
  }

  /**
   * Setup watchers for arrival-time and duration fields within route items.
   * This ensures calculateRouteDates is called when these nested fields change.
   */
  private setupRouteItemWatchers(): void {
    this.field.fieldGroup?.forEach(fieldGroup => {
      this.setupSingleRouteItemWatcher(fieldGroup);
    });
  }

  /**
   * Setup watcher for a single route item's arrival-time and duration fields.
   */
  private setupSingleRouteItemWatcher(fieldGroup: (typeof this.field.fieldGroup)[0]): void {
    const arrivalTimeField = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time');
    const durationField = fieldGroup.fieldGroup?.find(f => f.key === 'duration');

    if (arrivalTimeField?.formControl) {
      arrivalTimeField.formControl.valueChanges.pipe(debounceTime(300), takeUntil(this.#destroy$)).subscribe(() => {
        if (this.#isUpdatingTimes) return;
        this.#isUpdatingTimes = true;
        try {
          this.calculateRouteDates();
        } finally {
          this.#isUpdatingTimes = false;
        }
      });
    }

    if (durationField?.formControl) {
      durationField.formControl.valueChanges.pipe(debounceTime(300), takeUntil(this.#destroy$)).subscribe(() => {
        if (this.#isUpdatingTimes) return;
        this.#isUpdatingTimes = true;
        try {
          this.calculateRouteDates();
        } finally {
          this.#isUpdatingTimes = false;
        }
      });
    }
  }

  private getBaseDepartureDate(): Date | null {
    const fieldKeyPath = this.departureDateFieldKey();
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const dateField = parentFieldGroup.find(f => f.props?.keyPath === fieldKeyPath);
    const dateValue = dateField?.formControl?.value;

    if (dateValue) {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  calculateRouteDates(): void {
    const baseDate = this.baseDepartureDate();
    if (!baseDate) {
      this.calculatedDates.set(new Map());
      this.totalRouteDuration.set(null);
      return;
    }

    const newDates = new Map<number, RoutePointTime>();
    let previousEndTime: Date = baseDate;
    let previousArrivalTime: Date = baseDate;
    let currentDate = new Date(baseDate);

    this.field.fieldGroup?.forEach((fieldGroup, index) => {
      // Find form controls
      const arrivalTimeField = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time');
      const durationField = fieldGroup.fieldGroup?.find(f => f.key === 'duration');
      const arrivalTimeControl = arrivalTimeField?.formControl;

      // Read from formControl (not model) to get synced values after departure date change
      const arrivalTimeValue = arrivalTimeControl?.value;
      const durationValue = durationField?.formControl?.value;

      if (arrivalTimeValue) {
        // Extract time from the arrival-time value
        const timeString = DateHelperService.extractTimeFromDateTime(arrivalTimeValue);

        if (timeString) {
          const parsedTime = DateHelperService.parseTimeString(timeString);

          if (parsedTime) {
            // Check if we crossed midnight compared to previous arrival time
            const prevHours = previousArrivalTime.getHours();
            const prevMinutes = previousArrivalTime.getMinutes();
            const prevTotalMinutes = prevHours * 60 + prevMinutes;

            if (parsedTime.totalMinutes < prevTotalMinutes) {
              // Crossed midnight, add a day
              currentDate = DateHelperService.addDays(currentDate, 1);
            }

            // Create the calculated datetime
            const calculatedDateTime = new Date(currentDate);
            calculatedDateTime.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);

            const isNextDay = !DateHelperService.sameDay(calculatedDateTime, baseDate);
            const dateLabel = isNextDay ? DateHelperService.formatDateShortDutch(calculatedDateTime) : '';

            // Calculate minimum time based on previous arrival + duration
            const minimumTime = previousEndTime;
            const isBelowMinimum = calculatedDateTime.getTime() < minimumTime.getTime();

            newDates.set(index, {
              time: timeString,
              calculatedDate: calculatedDateTime,
              dateLabel,
              isNextDay,
              minimumTime,
              isBelowMinimum,
            });

            previousArrivalTime = calculatedDateTime;

            // Sync form control with calculated date (for midnight crossing)
            // Only update if the calculated date differs from stored value
            // Use emitEvent: true so datetime-input component updates its internal datePart
            if (arrivalTimeControl) {
              const newValue = DateHelperService.toLocalISOString(calculatedDateTime);
              if (newValue !== arrivalTimeValue) {
                arrivalTimeControl.setValue(newValue);
              }
            }

            // Calculate end time for this stop (arrival + duration)
            let endTime = calculatedDateTime;
            if (durationValue) {
              const durationMinutes = DateHelperService.timeStringToMinutes(durationValue);
              endTime = DateHelperService.addMinutes(calculatedDateTime, durationMinutes);
            }
            previousEndTime = endTime;
          }
        }
      }
    });

    this.calculatedDates.set(newDates);
    this.updateDestinationTime(previousEndTime);
    this.calculateTotalDuration();
  }

  /**
   * Update destination time field - ALWAYS sets the DATE portion automatically.
   * The date is calculated based on departure date or last route item (with midnight crossing priority).
   * The TIME portion is only preserved if user has already entered one.
   * If no time entered yet, sets date-only value so user sees "--:--".
   * Since the date field is hidden (hideDate: true), user only sees/enters the time.
   */
  private updateDestinationTime(calculatedEndDate: Date): void {
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === this.destinationTimeFieldKey());

    if (!destTimeField?.formControl) {
      return;
    }

    let currentValue = destTimeField.formControl.value;
    const baseDate = this.baseDepartureDate();

    if (!baseDate) {
      return;
    }

    // Determine the target date - last route item date has priority (accounts for midnight crossings)
    let targetDate: Date;
    if (calculatedEndDate && !isNaN(calculatedEndDate.getTime())) {
      targetDate = new Date(calculatedEndDate.getFullYear(), calculatedEndDate.getMonth(), calculatedEndDate.getDate());
    } else {
      targetDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
    }

    if (currentValue instanceof Date) {
      // If current value is a Date object, convert to ISO string
      currentValue = DateHelperService.toLocalISOString(currentValue);
    }

    // Helper: check if value is date-only (no 'T' separator)
    const isDateOnly = (value: string) => value && !value.includes('T');

    // Check if current value has a time component
    const currentHasTime = currentValue && !isDateOnly(currentValue);

    if (currentHasTime) {
      // User has already entered a time - preserve it, only update date
      const currentDate = new Date(currentValue);
      if (!isNaN(currentDate.getTime())) {
        let newDateTime = new Date(targetDate);
        newDateTime.setHours(currentDate.getHours(), currentDate.getMinutes(), 0, 0);

        // Handle midnight crossing: if destination time is before departure time,
        // the destination is on the next day
        if (newDateTime.getTime() <= baseDate.getTime()) {
          const destTimeMinutes = currentDate.getHours() * 60 + currentDate.getMinutes();
          const baseTimeMinutes = baseDate.getHours() * 60 + baseDate.getMinutes();

          // If destination time (e.g. 01:00) is earlier than departure time (e.g. 23:00),
          // it means we crossed midnight
          if (destTimeMinutes < baseTimeMinutes) {
            newDateTime = DateHelperService.addDays(newDateTime, 1);
          }
        }

        const newValue = DateHelperService.toLocalISOString(newDateTime);
        if (newValue !== currentValue) {
          destTimeField.formControl.setValue(newValue, { emitEvent: true });
        }
      }
    } else {
      // No time entered yet - set date-only value (shows "--:--" in UI)
      const dateOnlyValue = DateHelperService.dateObjectToDateString(targetDate);
      if (dateOnlyValue !== currentValue) {
        destTimeField.formControl.setValue(dateOnlyValue, { emitEvent: true });
      }
    }
  }

  private calculateTotalDuration(): void {
    const baseDate = this.baseDepartureDate();
    if (!baseDate) {
      this.totalRouteDuration.set(null);
      return;
    }

    // Try to get destination time from configured field
    const destTimeKeyPath = this.destinationTimeFieldKey();
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === destTimeKeyPath);
    let destTimeValue = destTimeField?.formControl?.value;

    if (destTimeValue instanceof Date) {
      // If current value is a Date object, convert to ISO string
      destTimeValue = DateHelperService.toLocalISOString(destTimeValue);
    }

    // Require eindbestemmingstijd to be filled (with time, not just date)
    // Date-only format: "YYYY-MM-DD" (10 chars, no T)
    // DateTime format: "YYYY-MM-DDTHH:mm..." (contains T)
    if (!destTimeValue || !destTimeValue.includes('T') || destTimeValue.length <= 10) {
      this.totalRouteDuration.set(null);
      return;
    }

    const endTime = new Date(destTimeValue);
    if (isNaN(endTime.getTime())) {
      this.totalRouteDuration.set(null);
      return;
    }

    // Handle midnight crossing: if end time appears to be before start time,
    // it means we've crossed midnight and need to add a day
    let adjustedEndTime = endTime;
    if (endTime.getTime() < baseDate.getTime()) {
      // Check if it's just the time that's earlier (crossed midnight)
      const baseTimeMinutes = baseDate.getHours() * 60 + baseDate.getMinutes();
      const endTimeMinutes = endTime.getHours() * 60 + endTime.getMinutes();

      // If end time is earlier in the day but same or earlier date, add days until it's after baseDate
      if (endTimeMinutes < baseTimeMinutes || DateHelperService.sameDay(baseDate, endTime)) {
        // Calculate how many days to add to make endTime after baseDate
        adjustedEndTime = new Date(endTime);
        while (adjustedEndTime.getTime() <= baseDate.getTime()) {
          adjustedEndTime = DateHelperService.addDays(adjustedEndTime, 1);
        }
      }
    }

    const diffMinutes = DateHelperService.calculateTimeDifferenceMinutes(baseDate, adjustedEndTime);

    if (diffMinutes < 0) {
      this.totalRouteDuration.set(null);
      return;
    }

    this.totalRouteDuration.set({
      totalMinutes: diffMinutes,
      formatted: DateHelperService.formatDurationMinutes(diffMinutes),
    });
  }

  /**
   * Synchronize all route arrival-times and destination time when departure date changes.
   * Keeps the time portion the same, only adjusts the date.
   * Preserves date-only format for values without time (shows "--:--").
   */
  private syncTimesWithDepartureDate(previousDate: Date, newDate: Date): void {
    // Calculate the day difference (in milliseconds)
    const dayDiffMs = this.calculateDateOnlyDifference(previousDate, newDate);

    if (dayDiffMs === 0) {
      return; // No date change, only time changed
    }

    // Helper: check if value is date-only (no 'T' separator)
    const isDateOnly = (value: string) => value && !value.includes('T');

    // Update all route item arrival-times
    this.field.fieldGroup?.forEach(fieldGroup => {
      const arrivalTimeControl = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time')?.formControl;
      if (arrivalTimeControl?.value) {
        const wasDateOnly = isDateOnly(arrivalTimeControl.value);
        const currentDateTime = new Date(arrivalTimeControl.value);
        if (!isNaN(currentDateTime.getTime())) {
          const newDateTime = new Date(currentDateTime.getTime() + dayDiffMs);
          // Preserve date-only format if original was date-only
          const newValue = wasDateOnly
            ? DateHelperService.dateObjectToDateString(newDateTime)
            : DateHelperService.toLocalISOString(newDateTime);
          arrivalTimeControl.setValue(newValue, { emitEvent: false, onlySelf: true });
        }
      }
    });

    // Update destination time field
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === this.destinationTimeFieldKey());
    if (destTimeField?.formControl?.value) {
      const wasDateOnly = isDateOnly(destTimeField.formControl.value);
      const currentDestTime = new Date(destTimeField.formControl.value);
      if (!isNaN(currentDestTime.getTime())) {
        const newDestTime = new Date(currentDestTime.getTime() + dayDiffMs);
        // Preserve date-only format if original was date-only
        const newValue = wasDateOnly
          ? DateHelperService.dateObjectToDateString(newDestTime)
          : DateHelperService.toLocalISOString(newDestTime);
        destTimeField.formControl.setValue(newValue, { emitEvent: false, onlySelf: true });
      }
    }
  }

  /**
   * Calculate the difference in days between two dates (ignoring time portion).
   * Returns difference in milliseconds.
   */
  private calculateDateOnlyDifference(date1: Date, date2: Date): number {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return d2.getTime() - d1.getTime();
  }

  // ==========================================
  // Helper Methods
  // ==========================================
  private getSiblingFieldValue(fieldKeyPath: string): unknown {
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    return parentFieldGroup.find(f => f.props?.keyPath === fieldKeyPath)?.formControl?.value;
  }

  /**
   * Refresh the destination time signal by reading from the form control.
   * This ensures the timeline updates when destination time is changed.
   */
  private refreshDestinationTime(): void {
    const destTimeKeyPath = this.destinationTimeFieldKey();
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === destTimeKeyPath);
    const destTimeValue = destTimeField?.formControl?.value;

    if (!destTimeValue) {
      this.#destinationTimeValue.set(null);
      return;
    }

    if (destTimeValue instanceof Date) {
      this.#destinationTimeValue.set(destTimeValue);
      return;
    }

    // Check if it's date-only (no time component)
    if (typeof destTimeValue === 'string' && !destTimeValue.includes('T')) {
      this.#destinationTimeValue.set(null);
      return;
    }

    const date = new Date(destTimeValue);
    this.#destinationTimeValue.set(isNaN(date.getTime()) ? null : date);
  }

  getDefaultModelForIndex(index: number): unknown {
    return this.#formService.getDefaultModelForIndex(this.field, index);
  }

  /**
   * Override add() to setup watchers for newly added route items.
   */
  override add(i?: number, initialModel?: unknown, opts?: { markAsDirty: boolean }): void {
    super.add(i, initialModel, opts);

    // Setup watcher for the newly added item after a small delay to ensure formControl is ready
    setTimeout(() => {
      const newIndex = i ?? this.field.fieldGroup.length - 1;
      const newFieldGroup = this.field.fieldGroup[newIndex];
      if (newFieldGroup) {
        this.setupSingleRouteItemWatcher(newFieldGroup);

        // Initialize arrival-time with calculated date (no time) so user sees "--:--"
        this.initializeArrivalTimeDate(newFieldGroup, newIndex);
      }
    }, 0);
  }

  /**
   * Initialize arrival-time with date-only value for newly added route items.
   * This ensures the user sees "--:--" for time and must fill it in.
   */
  private initializeArrivalTimeDate(fieldGroup: (typeof this.field.fieldGroup)[0], index: number): void {
    const arrivalTimeField = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time');
    if (!arrivalTimeField?.formControl || arrivalTimeField.formControl.value) {
      return; // Already has a value
    }

    let initialDate: Date | null = null;

    if (index > 0) {
      // Use previous route item's end time (arrival + duration)
      const prevFieldGroup = this.field.fieldGroup[index - 1];
      const prevArrivalTime = prevFieldGroup?.model?.['arrival-time'];
      const prevDuration = prevFieldGroup?.model?.['duration'];

      if (prevArrivalTime) {
        const prevDate = new Date(prevArrivalTime);
        if (!isNaN(prevDate.getTime())) {
          if (prevDuration) {
            const durationMinutes = DateHelperService.timeStringToMinutes(prevDuration);
            initialDate = DateHelperService.addMinutes(prevDate, durationMinutes);
          } else {
            initialDate = prevDate;
          }
        }
      }
    }

    // Fallback to base departure date
    if (!initialDate) {
      initialDate = this.baseDepartureDate();
    }

    if (initialDate) {
      // Set date-only value (format: YYYY-MM-DD) - UI will show "--:--" for time
      const dateOnlyValue = DateHelperService.dateObjectToDateString(initialDate);
      arrivalTimeField.formControl.setValue(dateOnlyValue);
    }
  }

  /**
   * Initialize all empty arrival-times when departure date is set for the first time.
   * This handles the case where route items were added before the departure date was set.
   */
  private initializeEmptyArrivalTimes(departureDate: Date): void {
    let previousEndTime = departureDate;

    this.field.fieldGroup?.forEach((fieldGroup, index) => {
      const arrivalTimeField = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time');
      const currentValue = arrivalTimeField?.formControl?.value;

      // Only initialize if empty (no value at all)
      if (arrivalTimeField?.formControl && !currentValue) {
        // Set date-only value based on previous end time
        const dateOnlyValue = DateHelperService.dateObjectToDateString(previousEndTime);
        arrivalTimeField.formControl.setValue(dateOnlyValue, { emitEvent: false, onlySelf: true });
      }

      // Update previousEndTime for next iteration
      if (currentValue) {
        const currentDate = new Date(currentValue);
        if (!isNaN(currentDate.getTime())) {
          const durationValue = fieldGroup.model?.['duration'];
          if (durationValue) {
            const durationMinutes = DateHelperService.timeStringToMinutes(durationValue);
            previousEndTime = DateHelperService.addMinutes(currentDate, durationMinutes);
          } else {
            previousEndTime = currentDate;
          }
        }
      }
    });

    // Also initialize destination-time if empty
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === this.destinationTimeFieldKey());
    if (destTimeField?.formControl && !destTimeField.formControl.value) {
      const dateOnlyValue = DateHelperService.dateObjectToDateString(previousEndTime);
      destTimeField.formControl.setValue(dateOnlyValue, { emitEvent: false, onlySelf: true });
    }
  }

  addInitialCount() {
    // Only apply defaultCount when creating a new entry (no existing data)
    if (this.model?.length > 0) {
      return;
    }

    const targetCount = this.props.defaultCount ?? this.props.initialCount;

    if (targetCount) {
      for (let i = 0; i < targetCount; i++) {
        this.add(i, this.getDefaultModelForIndex(i));
      }
    } else {
      this.props.initialCount = 0;
    }
  }

  trackByFn(index: number, item: unknown): number {
    return index;
  }

  /**
   * Helper to check if two dates are on the same day.
   * Used in template to determine if date label should be shown.
   */
  isSameDay(date1: Date | null, date2: Date | null): boolean {
    if (!date1 || !date2) return true;
    return DateHelperService.sameDay(date1, date2);
  }

  /**
   * Get the end time of a stop (arrival time + activity duration).
   * Returns null if no duration is set.
   */
  getStopEndTime(index: number): Date | null {
    const pointTime = this.calculatedDates().get(index);
    if (!pointTime?.calculatedDate) return null;

    const fieldGroup = this.field.fieldGroup?.[index];
    const durationValue = fieldGroup?.model?.['duration'];

    if (!durationValue) return null;

    const durationMinutes = DateHelperService.timeStringToMinutes(durationValue);
    if (durationMinutes <= 0) return null;

    return DateHelperService.addMinutes(pointTime.calculatedDate, durationMinutes);
  }
}
